/**
 * Fragment de routes du paquet v9-p02-abonnement (vague V9 ; fragment `v9-<sous>` par paquet, constat K11 de la fiche
 * V9). Composants chargés à la demande (règle de chargement du registre). Route du socle remplacée (`remplace: true`),
 * d'après le `couvre: route:/quotas` du YAML d'organisation-abonnement : `/quotas` → organisation-abonnement. Routes neuves :
 *  - `/org/abonnement/changer` → dialogue-changer-palier-organisation ;
 *  - `/org/abonnement/changements/:changementId` → organisation-abonnement-changement ;
 *  - `/org/abonnement/historique` → organisation-abonnement-historique ;
 *  - `/org/abonnement/resilier` → dialogue-resilier-abonnement ;
 *  - `/org/abonnement/consommation` → organisation-consommation ;
 *  - `/org/abonnement/moyens-paiement` et `/org/abonnement/moyens-paiement/ajouter` → organisation-moyens-paiement,
 *    dialogue-ajouter-moyen-paiement ;
 *  - `/plateforme/organisations/:organisationId/palier`, `…/palier/changer`, `…/consommation` →
 *    plateforme-organisation-palier, dialogue-changer-palier, plateforme-organisation-consommation.
 * Les écrans de plateforme reçoivent `superAdmin` (rôle de plateforme de la session) : le contexte de la règle de
 * plateforme en dépend, comme la garde serveur.
 */
import { lazy, Suspense, type ComponentType, type JSX } from 'react'

import type { Session } from '../../auth/session'
import type { ContexteRoutes, FragmentRoutes } from './registre'

const OrganisationAbonnement = lazy(() => import('../ecrans/v9/abonnement/OrganisationAbonnement'))
const OrganisationAbonnementChangement = lazy(() => import('../ecrans/v9/abonnement/OrganisationAbonnementChangement'))
const OrganisationAbonnementHistorique = lazy(() => import('../ecrans/v9/abonnement/OrganisationAbonnementHistorique'))
const OrganisationConsommation = lazy(() => import('../ecrans/v9/abonnement/OrganisationConsommation'))
const OrganisationMoyensPaiement = lazy(() => import('../ecrans/v9/abonnement/OrganisationMoyensPaiement'))
const DialogueAjouterMoyenPaiement = lazy(() => import('../ecrans/v9/abonnement/DialogueAjouterMoyenPaiement'))
const DialogueChangerPalierOrganisation = lazy(() => import('../ecrans/v9/abonnement/DialogueChangerPalierOrganisation'))
const DialogueResilierAbonnement = lazy(() => import('../ecrans/v9/abonnement/DialogueResilierAbonnement'))
const PlateformeOrganisationPalier = lazy(() => import('../ecrans/v9/abonnement/PlateformeOrganisationPalier'))
const DialogueChangerPalier = lazy(() => import('../ecrans/v9/abonnement/DialogueChangerPalier'))
const PlateformeOrganisationConsommation = lazy(() => import('../ecrans/v9/abonnement/PlateformeOrganisationConsommation'))

function ecran(Composant: ComponentType<{ session: Session; superAdmin?: boolean }>) {
  return ({ session, superAdmin, t }: ContexteRoutes): JSX.Element => (
    <Suspense fallback={<p role="status">{t('v10.primitives.assemblage.chargement')}</p>}>
      <Composant session={session} superAdmin={superAdmin} />
    </Suspense>
  )
}

export const fragment: FragmentRoutes = {
  zone: 'v9-abonnement',
  routes: [
    { chemin: '/quotas', element: ecran(OrganisationAbonnement), remplace: true },
    { chemin: '/org/abonnement/changer', element: ecran(DialogueChangerPalierOrganisation) },
    { chemin: '/org/abonnement/changements/:changementId', element: ecran(OrganisationAbonnementChangement) },
    { chemin: '/org/abonnement/historique', element: ecran(OrganisationAbonnementHistorique) },
    { chemin: '/org/abonnement/resilier', element: ecran(DialogueResilierAbonnement) },
    { chemin: '/org/abonnement/consommation', element: ecran(OrganisationConsommation) },
    { chemin: '/org/abonnement/moyens-paiement', element: ecran(OrganisationMoyensPaiement) },
    { chemin: '/org/abonnement/moyens-paiement/ajouter', element: ecran(DialogueAjouterMoyenPaiement) },
    { chemin: '/plateforme/organisations/:organisationId/palier', element: ecran(PlateformeOrganisationPalier) },
    { chemin: '/plateforme/organisations/:organisationId/palier/changer', element: ecran(DialogueChangerPalier) },
    { chemin: '/plateforme/organisations/:organisationId/consommation', element: ecran(PlateformeOrganisationConsommation) },
  ],
}
